import { gql, useMutation } from '@apollo/client';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Option,
  Select,
  Sheet,
} from '@mui/joy';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showMessage } from '../../store/snackbar-slice';
import { BED_SENSOR_COMMAND_CATALOG } from './command-catalog';

const sendBedSensorCommandMutation = gql`
  mutation SendBedSensorCommand($_id: ID!, $name: String!, $value: String) {
    sendBedSensorCommand(_id: $_id, name: $name, value: $value)
  }
`;

type CommandType = 'READ' | 'WRITE';

interface Props {
  _id: string;
}

function SendBedSensorCommandForm({ _id }: Props) {
  const [sendCommand] = useMutation(sendBedSensorCommandMutation);
  const [type, setType] = useState<CommandType>('READ');
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [sending, setSending] = useState(false);
  const dispatch = useDispatch();

  const selected = useMemo(
    () => BED_SENSOR_COMMAND_CATALOG.find((c) => c.name === name),
    [name],
  );

  const canSend = !!name && (type === 'READ' || !!value);

  const onSend = useCallback(async () => {
    if (!canSend) {
      return;
    }
    setSending(true);
    try {
      await sendCommand({
        variables: { _id, name, value: type === 'WRITE' ? value : null },
      });
      dispatch(
        showMessage({
          _id: 'send-bed-sensor-command',
          severity: 'success',
          message: 'Success',
        }),
      );
      setValue('');
    } catch (err: any) {
      console.error(err);
      dispatch(
        showMessage({
          _id: 'send-bed-sensor-command',
          severity: 'danger',
          message: 'There was an error sending the command.',
        }),
      );
    }
    setSending(false);
  }, [canSend, sendCommand, dispatch, _id, type, name, value]);

  return (
    <Sheet variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 'md' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <FormControl>
          <FormLabel>Action</FormLabel>
          <Select
            value={type}
            onChange={(_e, v) => v && setType(v as CommandType)}
            sx={{ minWidth: 120 }}
          >
            <Option value="READ">Read</Option>
            <Option value="WRITE">Write</Option>
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Command</FormLabel>
          <Select
            value={name || null}
            onChange={(_e, v) => setName((v as string) || '')}
            placeholder="Select a command"
            sx={{ minWidth: 220 }}
          >
            {BED_SENSOR_COMMAND_CATALOG.map((c) => (
              <Option key={c.name} value={c.name}>
                {c.name}
                {c.unit ? ` (${c.unit})` : ''}
              </Option>
            ))}
          </Select>
        </FormControl>
        {type === 'WRITE' && (
          <FormControl>
            <FormLabel>
              Value{selected?.unit ? ` (${selected.unit})` : ''}
            </FormLabel>
            <Input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              sx={{ minWidth: 160 }}
            />
          </FormControl>
        )}
        <Button loading={sending} disabled={!canSend} onClick={onSend}>
          Send
        </Button>
      </Box>
      {selected?.critical && (
        <Alert color="danger" startDecorator={<WarningRoundedIcon />} sx={{ mt: 2 }}>
          {selected.name} can affect the device&apos;s firmware or its connection to
          the server. Double-check the value before sending.
        </Alert>
      )}
    </Sheet>
  );
}

export default SendBedSensorCommandForm;
